import { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { db } from '../../lib/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import {
  storeFollowUpBossApiKey,
  setupCustomFields,
  getCustomFields,
  validateCustomFields,
  updateFubSetupData,
  pullStages,
  getStageAllowlist,
  setStageAllowlist,
  checkStagePermission,
  pullLeadSources,
  syncTeamMembers,
  registerWebhooks,
  getWebhookStatus,
  unregisterWebhooks,
} from '../../api/followUpBoss';

export const useFollowUpBossOperations = () => {
  // Loading states
  const [isStoringApiKey, setIsStoringApiKey] = useState(false);
  const [isSettingUpCustomFields, setIsSettingUpCustomFields] = useState(false);
  const [isFetchingCustomFields, setIsFetchingCustomFields] = useState(false);
  const [isValidatingCustomFields, setIsValidatingCustomFields] = useState(false);
  const [isUpdatingFubSetupData, setIsUpdatingFubSetupData] = useState(false);
  const [isPullingStages, setIsPullingStages] = useState(false);
  const [isFetchingAllowlist, setIsFetchingAllowlist] = useState(false);
  const [isSettingAllowlist, setIsSettingAllowlist] = useState(false);
  const [isCheckingStagePermission, setIsCheckingStagePermission] = useState(false);
  const [isPullingLeadSources, setIsPullingLeadSources] = useState(false);
  const [isSyncingTeamMembers, setIsSyncingTeamMembers] = useState(false);
  const [isRegisteringWebhooks, setIsRegisteringWebhooks] = useState(false);
  const [isFetchingWebhookStatus, setIsFetchingWebhookStatus] = useState(false);
  const [isUnregisteringWebhooks, setIsUnregisteringWebhooks] = useState(false);
  const [isSavingSubdomain, setIsSavingSubdomain] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const { showToast } = useToast();

  /**
   * Store the Follow Up Boss API key for an organization
   * @param orgId The organization ID
   * @param apiKey The API key to store
   * @param isFirstTimeSetup Whether this is the first time setting up the API key
   */
  const storeApiKey = async (
    orgId: string,
    apiKey: string,
    isFirstTimeSetup: boolean = false
  ): Promise<boolean> => {
    if (!orgId || !apiKey) {
      showToast({
        message: 'Organization ID and API Key are required',
        variant: 'error',
      });
      return false;
    }

    setIsStoringApiKey(true);
    try {
      // Store the API key
      const success = await storeFollowUpBossApiKey(orgId, apiKey);

      return success;
    } catch (error) {
      console.error('Error storing Follow Up Boss API key:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to store API key',
        variant: 'error',
      });
      return false;
    } finally {
      setIsStoringApiKey(false);
    }
  };

  /**
   * Setup custom fields in Follow Up Boss for an organization
   */
  const setupFubCustomFields = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsSettingUpCustomFields(true);
    try {
      const result = await setupCustomFields(orgId);
      if (result.success) {
        // Save the custom fields to the organization document
        if (
          result.results &&
          (Array.isArray(result.results.existing) || Array.isArray(result.results.created))
        ) {
          const customfields = [
            ...(Array.isArray(result.results.existing) ? result.results.existing : []),
            ...(Array.isArray(result.results.created) ? result.results.created : []),
          ];

          if (customfields.length > 0) {
            await updateFubSetupData(orgId, { customfields });
          }
        }

        showToast({
          message: 'Custom fields setup completed successfully',
          variant: 'success',
        });
      } else {
        showToast({
          message: 'Failed to setup custom fields',
          variant: 'error',
        });
      }
      return result;
    } catch (error) {
      console.error('Error setting up custom fields:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to setup custom fields',
        variant: 'error',
      });
      return null;
    } finally {
      setIsSettingUpCustomFields(false);
    }
  };

  /**
   * Update FUB setup data in the organization document
   */
  const updateFubSetupDataInOrg = async (
    orgId: string,
    setupData: { customfields?: string[]; allowedStages?: string[] }
  ) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return false;
    }

    setIsUpdatingFubSetupData(true);
    try {
      const success = await updateFubSetupData(orgId, setupData);
      return success;
    } catch (error) {
      console.error('Error updating FUB setup data:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update FUB setup data',
        variant: 'error',
      });
      return false;
    } finally {
      setIsUpdatingFubSetupData(false);
    }
  };

  /**
   * Get custom fields from Follow Up Boss for an organization
   */
  const getFubCustomFields = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsFetchingCustomFields(true);
    try {
      return await getCustomFields(orgId);
    } catch (error) {
      console.error('Error getting custom fields:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to get custom fields',
        variant: 'error',
      });
      return null;
    } finally {
      setIsFetchingCustomFields(false);
    }
  };

  /**
   * Validate custom fields in Follow Up Boss for an organization
   */
  const validateFubCustomFields = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsValidatingCustomFields(true);
    try {
      return await validateCustomFields(orgId);
    } catch (error) {
      console.error('Error validating custom fields:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to validate custom fields',
        variant: 'error',
      });
      return null;
    } finally {
      setIsValidatingCustomFields(false);
    }
  };

  /**
   * Pull stages from Follow Up Boss for an organization
   */
  const pullFubStages = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsPullingStages(true);
    try {
      return await pullStages(orgId);
    } catch (error) {
      console.error('Error pulling stages:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to pull stages',
        variant: 'error',
      });
      return null;
    } finally {
      setIsPullingStages(false);
    }
  };

  /**
   * Get stage allowlist from Follow Up Boss for an organization
   */
  const getFubStageAllowlist = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsFetchingAllowlist(true);
    try {
      return await getStageAllowlist(orgId);
    } catch (error) {
      console.error('Error getting stage allowlist:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to get stage allowlist',
        variant: 'error',
      });
      return null;
    } finally {
      setIsFetchingAllowlist(false);
    }
  };

  /**
   * Set stage allowlist for Follow Up Boss for an organization
   */
  const setFubStageAllowlist = async (
    orgId: string,
    allowedStages: string[],
    existingFubSetup?: any
  ) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsSettingAllowlist(true);
    try {
      // Call the API to update the allowlist in the backend
      const result = await setStageAllowlist(orgId, allowedStages);

      if (result.success) {
        // Get existing FUB setup data to preserve custom fields
        const existingSetupData = existingFubSetup || {};

        // Save the allowlist to Firestore
        const firestoreSuccess = await updateFubSetupData(orgId, {
          ...existingSetupData,
          allowedStages: result.allowedStages || allowedStages,
        });

        if (firestoreSuccess) {
          showToast({
            message: 'Stage allowlist updated successfully',
            variant: 'success',
          });
        }
      } else {
        showToast({
          message: 'Failed to update stage allowlist',
          variant: 'error',
        });
      }
      return result;
    } catch (error) {
      console.error('Error setting stage allowlist:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to set stage allowlist',
        variant: 'error',
      });
      return null;
    } finally {
      setIsSettingAllowlist(false);
    }
  };

  /**
   * Check if a stage is allowed for AI engagement
   */
  const checkFubStagePermission = async (orgId: string, stageName: string) => {
    if (!orgId || !stageName) {
      showToast({
        message: 'Organization ID and stage name are required',
        variant: 'error',
      });
      return null;
    }

    setIsCheckingStagePermission(true);
    try {
      return await checkStagePermission(orgId, stageName);
    } catch (error) {
      console.error('Error checking stage permission:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to check stage permission',
        variant: 'error',
      });
      return null;
    } finally {
      setIsCheckingStagePermission(false);
    }
  };

  /**
   * Pull lead sources from Follow Up Boss for an organization
   */
  const pullFubLeadSources = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsPullingLeadSources(true);
    try {
      return await pullLeadSources(orgId);
    } catch (error) {
      console.error('Error pulling lead sources:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to pull lead sources',
        variant: 'error',
      });
      return null;
    } finally {
      setIsPullingLeadSources(false);
    }
  };

  /**
   * Sync team members from Follow Up Boss to Speculo
   */
  const syncFubTeamMembers = async (orgId: string, createMissing: boolean = true) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsSyncingTeamMembers(true);
    try {
      const result = await syncTeamMembers(orgId, createMissing);
      if (result.success) {
        // Update the organization document to mark team members as synced
        try {
          const orgRef = doc(db, 'Organizations', orgId);

          // Get existing fubSetup data
          const orgDoc = await getDoc(orgRef);
          const orgData = orgDoc.data();
          const existingFubSetup = orgData?.fubSetup || {};

          // Update with teamMembers flag
          await updateDoc(orgRef, {
            fubSetup: {
              ...existingFubSetup,
              teamMembers: true,
            },
          });

          showToast({
            message: 'Team members synced successfully',
            variant: 'success',
          });
        } catch (updateError) {
          console.error('Error updating organization document:', updateError);
          showToast({
            message: 'Team members synced but failed to update organization status',
            variant: 'warning',
          });
        }
      } else {
        showToast({
          message: 'Failed to sync team members',
          variant: 'error',
        });
      }
      return result;
    } catch (error) {
      console.error('Error syncing team members:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to sync team members',
        variant: 'error',
      });
      return null;
    } finally {
      setIsSyncingTeamMembers(false);
    }
  };

  /**
   * Register webhooks for Follow Up Boss for an organization
   */
  const registerFubWebhooks = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsRegisteringWebhooks(true);
    try {
      const result = await registerWebhooks(orgId);
      if (result.success) {
        // Update the organization document to mark webhooks as set up
        try {
          const orgRef = doc(db, 'Organizations', orgId);
          const orgDoc = await getDoc(orgRef);
          const orgData = orgDoc.data();
          const existingFubSetup = orgData?.fubSetup || {};

          // Update with webHooks flag
          await updateDoc(orgRef, {
            fubSetup: {
              ...existingFubSetup,
              webHooks: true,
            },
          });

          showToast({
            message: 'Webhooks registered successfully',
            variant: 'success',
          });
        } catch (updateError) {
          console.error('Error updating organization document:', updateError);
          showToast({
            message: 'Webhooks registered but failed to update organization status',
            variant: 'warning',
          });
        }
      } else {
        showToast({
          message: 'Failed to register webhooks',
          variant: 'error',
        });
      }
      return result;
    } catch (error) {
      console.error('Error registering webhooks:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to register webhooks',
        variant: 'error',
      });
      return null;
    } finally {
      setIsRegisteringWebhooks(false);
    }
  };

  /**
   * Get webhook status for Follow Up Boss for an organization
   */
  const getFubWebhookStatus = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsFetchingWebhookStatus(true);
    try {
      return await getWebhookStatus(orgId);
    } catch (error) {
      console.error('Error getting webhook status:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to get webhook status',
        variant: 'error',
      });
      return null;
    } finally {
      setIsFetchingWebhookStatus(false);
    }
  };

  /**
   * Unregister webhooks for Follow Up Boss for an organization
   */
  const unregisterFubWebhooks = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return null;
    }

    setIsUnregisteringWebhooks(true);
    try {
      const result = await unregisterWebhooks(orgId);
      if (result.success) {
        showToast({
          message: 'Webhooks unregistered successfully',
          variant: 'success',
        });
      } else {
        showToast({
          message: 'Failed to unregister webhooks',
          variant: 'error',
        });
      }
      return result;
    } catch (error) {
      console.error('Error unregistering webhooks:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to unregister webhooks',
        variant: 'error',
      });
      return null;
    } finally {
      setIsUnregisteringWebhooks(false);
    }
  };

  /**
   * Disconnect and reset Follow Up Boss integration
   * Clears all FUB setup data, API keys, and CRM connection if it's FollowUpBoss
   */
  const disconnectFub = async (orgId: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return false;
    }

    setIsDisconnecting(true);
    try {
      const orgRef = doc(db, 'Organizations', orgId);

      // Get current organization data to check connectedCRM
      const orgDoc = await getDoc(orgRef);
      const orgData = orgDoc.data();

      // Prepare update object
      const updateData: any = {
        fubSetup: {}, // Clear all FUB setup data
        crmApiKey: '', // Clear API key
        crmApiKeyHash: '', // Clear API key hash
      };

      // Only clear connectedCRM if it's "followupboss"
      if (orgData?.connectedCRM === 'followupboss') {
        updateData.connectedCRM = '';
      }

      // Update the organization document
      await updateDoc(orgRef, updateData);

      showToast({
        message: 'Follow Up Boss integration disconnected successfully',
        variant: 'success',
      });

      return true;
    } catch (error) {
      console.error('Error disconnecting Follow Up Boss:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to disconnect Follow Up Boss',
        variant: 'error',
      });
      return false;
    } finally {
      setIsDisconnecting(false);
    }
  };

  /**
   * Save subdomain for Follow Up Boss integration
   */
  const saveFubSubdomain = async (orgId: string, subdomain: string) => {
    if (!orgId) {
      showToast({
        message: 'Organization ID is required',
        variant: 'error',
      });
      return false;
    }

    const trimmedSubdomain = subdomain.trim();

    if (!trimmedSubdomain) {
      showToast({
        message: 'Subdomain cannot be empty',
        variant: 'error',
      });
      return false;
    }

    setIsSavingSubdomain(true);
    try {
      const orgRef = doc(db, 'Organizations', orgId);

      // Get existing fubSetup data to preserve other fields
      const orgDoc = await getDoc(orgRef);
      const orgData = orgDoc.data();
      const existingFubSetup = orgData?.fubSetup || {};

      // Merge with existing data
      await updateDoc(orgRef, {
        fubSetup: {
          ...existingFubSetup,
          subdomain: trimmedSubdomain,
        },
      });

      showToast({
        message: 'Subdomain saved successfully',
        variant: 'success',
      });

      return true;
    } catch (error) {
      console.error('Error saving subdomain:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save subdomain',
        variant: 'error',
      });
      return false;
    } finally {
      setIsSavingSubdomain(false);
    }
  };

  return {
    // API key operations
    storeApiKey,
    isStoringApiKey,

    // Custom fields operations
    setupFubCustomFields,
    getFubCustomFields,
    validateFubCustomFields,
    isSettingUpCustomFields,
    isFetchingCustomFields,
    isValidatingCustomFields,

    // Stage operations
    pullFubStages,
    getFubStageAllowlist,
    setFubStageAllowlist,
    checkFubStagePermission,
    isPullingStages,
    isFetchingAllowlist,
    isSettingAllowlist,
    isCheckingStagePermission,

    // Lead sources operations
    pullFubLeadSources,
    isPullingLeadSources,

    // Team members operations
    syncFubTeamMembers,
    isSyncingTeamMembers,

    // Webhook operations
    registerFubWebhooks,
    getFubWebhookStatus,
    unregisterFubWebhooks,
    isRegisteringWebhooks,
    isFetchingWebhookStatus,
    isUnregisteringWebhooks,

    // Subdomain operations
    saveFubSubdomain,
    isSavingSubdomain,

    // Disconnect operations
    disconnectFub,
    isDisconnecting,
  };
};
